import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Linkedin, Users } from 'lucide-react';
import { Button } from "@/components/ui/button";

const callers = [
  { name: 'Babacar' },
  { name: 'Célim' },
  { name: 'Marie' },
  { name: 'Cia' },
  { name: 'Adam' },
  { name: 'Lina' },
  { name: 'Omar' },
  { name: 'Inès' },
  { name: 'Théo' },
];

const positiveEmojis = ["🚀","💪","🌟","😄","🔥","🎯","🤝","🏆","😊","👍","🤩","☀️","✨","📈","💥","🥇","💫","🎉","📞","🤗"];
const gradients = [
  "from-indigo-500 to-purple-500",
  "from-emerald-500 to-teal-500",
  "from-rose-500 to-orange-500",
  "from-sky-500 to-cyan-500",
  "from-violet-500 to-fuchsia-500",
  "from-amber-500 to-yellow-500",
  "from-blue-500 to-indigo-500",
  "from-pink-500 to-rose-500",
  "from-green-500 to-lime-500",
  "from-cyan-500 to-teal-500",
];

export default function TeamSection() {
  const { t } = useTranslation(['admin']);
  return (
    <section id="team" className="relative py-32 bg-[#0a0a0a]">
      {/* Gradient line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      {/* Background glow */}
      <div className="pointer-events-none absolute -top-10 right-0 w-[420px] h-[420px] bg-fuchsia-600/10 rounded-full blur-[140px]" />

      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-purple-400 mb-3 block">{t('admin:landing.team.label')}</span>
          <h2 className="text-3xl md:text-5xl font-bold text-white">{t('admin:landing.team.heading')}</h2>
          <p className="text-white/40 mt-3 max-w-2xl mx-auto">{t('admin:landing.team.subheading')}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          {/* Founder */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <div className="flex items-start gap-6">
              <div className="relative shrink-0">
                <div className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden ring-2 ring-purple-500/50">
                  <img
                    src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692d6394a23762baf1ff05b8/772053661_17291784123151.jpg"
                    // i18n-exempt: the founder's name
                    alt="Pierre-Jean Pouhyet"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-3 -right-3 w-24 h-24 bg-gradient-to-br from-purple-500 to-fuchsia-500 rounded-2xl -z-10 blur-2xl opacity-50" />
              </div>

              <div className="pt-2">
                <span className="text-xs uppercase tracking-[0.2em] text-purple-400 mb-2 block">{t('admin:landing.team.founderLabel')}</span>
                {/* i18n-exempt: the founder's name */}
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">Pierre-Jean <span className="bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent">Pouhyet</span></h3>
                <p className="text-white/50 leading-relaxed mb-5 max-w-lg">
                  {t('admin:landing.team.founderBio')}
                </p>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-full w-10 h-10 border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white"
                    asChild
                  >
                    <a href="https://linkedin.com/in/pj-pouhyet" target="_blank" rel="noopener noreferrer">
                      <Linkedin className="w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Callers cloud */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <div className="rounded-3xl bg-gradient-to-b from-white/[0.04] to-white/[0.02] backdrop-blur-sm border border-white/[0.08] p-6 md:p-8 shadow-xl shadow-black/20">
              <div className="flex items-center gap-2 pb-4 mb-6 border-b border-white/[0.06]">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold">{t('admin:landing.team.coldCallersTitle')}</p>
                  <p className="text-white/50 text-sm">{t('admin:landing.team.coldCallersSubtitle')}</p>
                </div>
              </div>

              {/* Grid of bubbles */}
              <div className="grid grid-cols-3 gap-6">
                {callers.map((c, i) => (
                  <motion.div
                    key={c.name}
                    initial={{ opacity: 0, scale: 0.9, y: 10 }}
                    whileInView={{ opacity: 1, scale: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.03 }}
                    className="flex flex-col items-center"
                  >
                    <div className="relative group">
                      <div className={`w-20 h-20 sm:w-20 sm:h-20 rounded-full ring-1 ring-white/10 group-hover:ring-emerald-400/50 transition-all duration-300 shadow-lg shadow-black/30 bg-gradient-to-br ${gradients[i % gradients.length]} flex items-center justify-center text-xl`}>
                        <span className="select-none">{positiveEmojis[i % positiveEmojis.length]}</span>
                      </div>
                      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-black/40 backdrop-blur-sm border border-white/10 text-white/80 text-[10px] sm:text-xs whitespace-nowrap">
                        {c.name}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}